import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getCategoryBySlug, categories } from '@/mocks/categories';
import { getArticlesByCategory, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-foreground-400 text-4xl"></i>
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">分类未找到</h2>
            <p className="text-foreground-600 text-sm mb-6">该分类不存在或已被移除</p>
            <Link
              to="/"
              className="whitespace-nowrap cursor-pointer inline-block px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 51fun吃瓜黑料不打烊`,
    description: category.description,
    url: `http://oheaven.cn/category/${category.slug}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categoryJsonLd)}</script>
      <Navbar />
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Mysterious%20dark%20abstract%20background%20with%20deep%20green%20and%20charcoal%20gradients%2C%20dramatic%20cinematic%20lighting%20with%20soft%20golden%20amber%20accents%2C%20elegant%20and%20moody%20atmosphere%20with%20subtle%20geometric%20patterns%2C%20editorial%20design%20background%20ideal%20for%20entertainment%20news%2C%20no%20text%20no%20people&width=1600&height=900&seq=category-hero-general-v1&orientation=landscape"
              alt={`${category.name} - 51fun吃瓜黑料不打烊`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6">
            <div className="text-center">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-3">
                {category.name}
              </h1>
              <p className="text-background-50/70 text-sm md:text-base max-w-lg mx-auto">
                {category.description}
              </p>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 pb-6 pointer-events-none">
            <p className="text-center text-background-50/10 font-heading font-bold text-2xl md:text-4xl whitespace-nowrap tracking-wider">
              {category.name} · 黑料吃瓜
            </p>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />
        </div>

        {/* Articles */}
        <section className="py-10 md:py-16 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-background-300/60 transition-all cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-secondary-900/70 text-background-50 text-xs whitespace-nowrap">
                        {article.category}
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-label text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-700 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-foreground-600 text-xs leading-relaxed mb-4 line-clamp-2">
                        {article.summary}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-foreground-500 text-xs">{article.publishedAt}</span>
                        <div className="flex items-center gap-3 text-foreground-500 text-xs">
                          <span className="flex items-center gap-1">
                            <div className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-eye-line text-[10px]"></i>
                            </div>
                            {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
                          </span>
                          <span className="flex items-center gap-1">
                            <div className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-chat-3-line text-[10px]"></i>
                            </div>
                            {article.comments}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-inbox-line text-foreground-400 text-3xl"></i>
                </div>
                <p className="text-foreground-600 text-sm mb-4">该分类暂无文章，敬请期待</p>
                <Link
                  to="/"
                  className="whitespace-nowrap cursor-pointer inline-block px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Other Categories */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">探索更多分类</h2>
            <div className="flex flex-wrap gap-3">
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="whitespace-nowrap cursor-pointer px-5 py-2.5 rounded-full bg-background-50 border border-background-200/70 text-foreground-700 text-sm hover:border-accent-400 hover:text-accent-600 transition-all"
                  >
                    <div className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    {cat.name}
                    {cat.count > 0 && (
                      <span className="ml-1.5 text-foreground-400 text-xs">({cat.count})</span>
                    )}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}